import { useState } from "react";
import { PageHeader, Disclaimer } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Upload, ArrowLeft, ArrowRight, CheckCircle2, FileText } from "lucide-react";
import apiClient from "@/lib/api";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";

const STEPS = ["Your Profile", "Education & Work", "Pathway & Documents", "Review & Submit"];

const initial = {
  full_name: "", email: "", phone: "", current_country: "",
  current_visa_status: "", age: "", marital_status: "",
  partner_details: "", children_details: "",
  highest_qualification: "", australian_study_history: "",
  occupation: "", years_experience: "", skills_assessment_status: "",
  english_test_result: "", preferred_visa_pathway: "",
  previous_refusal: "", art_matter: "",
  cv_file_path: "", cv_filename: "",
  message: "", consent: false,
};

export default function PRAssessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const uploadCV = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: res } = await apiClient.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("cv_file_path", res.path);
      update("cv_filename", res.filename);
      toast.success("CV uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!data.consent) {
      toast.error("Please confirm consent to submit.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        age: data.age ? parseInt(data.age) : null,
        years_experience: data.years_experience ? parseInt(data.years_experience) : null,
        consent_timestamp: new Date().toISOString(),
      };
      await apiClient.post("/pr-assessment", payload);
      setDone(true);
      toast.success("Assessment submitted. We will respond within 1–2 business days.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHeader eyebrow="Submitted" title="Thank you — we've received your PR assessment." subtitle="One of our Registered Migration Agents will personally review your details and respond by email within 1–2 business days." />
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#C43A89] mx-auto mb-6" />
            <h2 className="font-display text-3xl text-[#29324A]">What happens next</h2>
            <ul className="mt-8 text-left space-y-4 text-slate-700 max-w-2xl mx-auto">
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">01</span><span>We review your profile against current visa categories and Department guidance.</span></li>
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">02</span><span>You receive an initial response by email outlining viable pathways.</span></li>
              <li className="flex gap-3"><span className="text-[#C43A89] font-bold">03</span><span>If suitable, you may book a paid consultation for tailored strategy.</span></li>
            </ul>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="pr-assessment-page">
      <PageHeader
        eyebrow="Offshore Visa Assessment"
        title="Tell us about your migration goals."
        subtitle="For applicants currently outside Australia. A confidential, no-obligation assessment by a Registered Migration Agent (MARN 2619415 · IAA Licence No. 202603837). It takes about 5 minutes."
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stepper */}
          <div className="mb-10">
            <div className="flex items-center justify-between text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">
              <span data-testid="pr-current-step">Step {step + 1} / {STEPS.length}: <span className="text-[#29324A]">{STEPS[step]}</span></span>
              <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
            </div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5 bg-slate-100" />
          </div>

          {step === 0 && (
            <div data-testid="step-profile" className="space-y-6 fade-up">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name *"><Input data-testid="input-full_name" value={data.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Jane Doe" /></Field>
                <Field label="Email *"><Input data-testid="input-email" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" /></Field>
                <Field label="Phone *"><Input data-testid="input-phone" value={data.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+61 ..." /></Field>
                <Field label="Current country *"><Input data-testid="input-country" value={data.current_country} onChange={(e) => update("current_country", e.target.value)} placeholder="e.g. Australia" /></Field>
                <Field label="Current visa status"><Input data-testid="input-visa-status" value={data.current_visa_status} onChange={(e) => update("current_visa_status", e.target.value)} placeholder="e.g. 500 Student" /></Field>
                <Field label="Age"><Input data-testid="input-age" type="number" value={data.age} onChange={(e) => update("age", e.target.value)} /></Field>
                <Field label="Marital status">
                  <Select value={data.marital_status} onValueChange={(v) => update("marital_status", v)}>
                    <SelectTrigger data-testid="select-marital"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married / De-facto</SelectItem>
                      <SelectItem value="separated">Separated</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Partner details (if any)"><Textarea data-testid="input-partner" value={data.partner_details} onChange={(e) => update("partner_details", e.target.value)} placeholder="Age, qualifications, English level, citizenship" rows={2} /></Field>
              <Field label="Children details (if any)"><Textarea data-testid="input-children" value={data.children_details} onChange={(e) => update("children_details", e.target.value)} placeholder="Number of children, ages, citizenship" rows={2} /></Field>
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-education" className="space-y-6 fade-up">
              <Field label="Highest qualification">
                <Select value={data.highest_qualification} onValueChange={(v) => update("highest_qualification", v)}>
                  <SelectTrigger data-testid="select-qualification"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phd">Doctorate</SelectItem>
                    <SelectItem value="masters">Masters</SelectItem>
                    <SelectItem value="bachelors">Bachelors</SelectItem>
                    <SelectItem value="diploma">Diploma / Advanced Diploma</SelectItem>
                    <SelectItem value="trade">Trade / AQF III/IV</SelectItem>
                    <SelectItem value="secondary">Secondary School</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Australian study history"><Textarea data-testid="input-aus-study" value={data.australian_study_history} onChange={(e) => update("australian_study_history", e.target.value)} placeholder="Courses, providers, dates, regional study (if any)" rows={3} /></Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Occupation"><Input data-testid="input-occupation" value={data.occupation} onChange={(e) => update("occupation", e.target.value)} placeholder="e.g. Software Engineer" /></Field>
                <Field label="Years of work experience"><Input data-testid="input-experience" type="number" value={data.years_experience} onChange={(e) => update("years_experience", e.target.value)} /></Field>
                <Field label="Skills assessment status">
                  <Select value={data.skills_assessment_status} onValueChange={(v) => update("skills_assessment_status", v)}>
                    <SelectTrigger data-testid="select-skills"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positive">Positive assessment</SelectItem>
                      <SelectItem value="pending">Application pending</SelectItem>
                      <SelectItem value="not_started">Not started</SelectItem>
                      <SelectItem value="not_required">Not required</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="English test result">
                  <Select value={data.english_test_result} onValueChange={(v) => update("english_test_result", v)}>
                    <SelectTrigger data-testid="select-english"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="superior">Superior (e.g. IELTS 8+)</SelectItem>
                      <SelectItem value="proficient">Proficient (e.g. IELTS 7)</SelectItem>
                      <SelectItem value="competent">Competent (e.g. IELTS 6)</SelectItem>
                      <SelectItem value="not_taken">Not taken yet</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-pathway" className="space-y-6 fade-up">
              <Field label="Preferred visa pathway">
                <Select value={data.preferred_visa_pathway} onValueChange={(v) => update("preferred_visa_pathway", v)}>
                  <SelectTrigger data-testid="select-pathway"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="189">Subclass 189 — Skilled Independent</SelectItem>
                    <SelectItem value="190">Subclass 190 — State Nominated</SelectItem>
                    <SelectItem value="491">Subclass 491 — Regional</SelectItem>
                    <SelectItem value="482">Subclass 482 — Skills in Demand</SelectItem>
                    <SelectItem value="186">Subclass 186 — ENS</SelectItem>
                    <SelectItem value="partner">Partner Visa</SelectItem>
                    <SelectItem value="485">Subclass 485 — Graduate</SelectItem>
                    <SelectItem value="unsure">Unsure — please advise</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Previous visa refusal or cancellation"><Textarea data-testid="input-refusal" value={data.previous_refusal} onChange={(e) => update("previous_refusal", e.target.value)} placeholder="Visa, date, reason (if known)" rows={2} /></Field>
              <Field label="ART matter (if any)"><Textarea data-testid="input-art" value={data.art_matter} onChange={(e) => update("art_matter", e.target.value)} placeholder="Brief summary" rows={2} /></Field>

              <Field label="Upload CV (PDF/DOC, max 10 MB)">
                <label className="border-2 border-dashed border-slate-300 hover:border-[#29324A] p-6 flex flex-col items-center justify-center cursor-pointer transition-colors text-center">
                  <Upload className="h-7 w-7 text-[#29324A] mb-3" />
                  <span className="text-sm font-semibold text-[#29324A]">
                    {data.cv_filename ? data.cv_filename : "Click to upload your CV"}
                  </span>
                  <span className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX — up to 10 MB</span>
                  <input
                    data-testid="input-cv"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => uploadCV(e.target.files?.[0])}
                  />
                </label>
                {uploading && <div className="text-xs text-slate-500 mt-2">Uploading...</div>}
                {data.cv_filename && !uploading && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700"><FileText className="h-4 w-4" /> {data.cv_filename}</div>
                )}
              </Field>

              <Field label="Additional message"><Textarea data-testid="input-message" value={data.message} onChange={(e) => update("message", e.target.value)} placeholder="Anything else we should know" rows={4} /></Field>
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-review" className="space-y-6 fade-up">
              <h3 className="font-display text-2xl text-[#29324A]">Review your details</h3>
              <div className="border border-slate-200 divide-y divide-slate-200 text-sm">
                {Object.entries({
                  "Full name": data.full_name, "Email": data.email, "Phone": data.phone,
                  "Current country": data.current_country, "Visa status": data.current_visa_status,
                  "Age": data.age, "Marital status": data.marital_status,
                  "Highest qualification": data.highest_qualification,
                  "Australian study": data.australian_study_history,
                  "Occupation": data.occupation, "Years experience": data.years_experience,
                  "Skills assessment": data.skills_assessment_status,
                  "English": data.english_test_result, "Preferred pathway": data.preferred_visa_pathway,
                  "Previous refusal": data.previous_refusal, "ART matter": data.art_matter,
                  "CV": data.cv_filename, "Message": data.message,
                }).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 px-4 py-2.5">
                    <span className="font-semibold text-[#29324A]">{k}</span>
                    <span className="col-span-2 text-slate-600">{v || "—"}</span>
                  </div>
                ))}
              </div>

              <Disclaimer>
                This assessment is general guidance only. It does not constitute personalised migration advice and does not guarantee any visa outcome. For tailored advice, please book a paid consultation.
              </Disclaimer>

              <ConsentCheckbox
                value={data.consent}
                onChange={(v) => update("consent", v)}
                testid="consent-checkbox"
                showDisclaimer={false}
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-10 pt-6 border-t border-slate-200">
            <Button
              data-testid="pr-back-btn"
              type="button"
              onClick={back}
              disabled={step === 0}
              className="bg-transparent border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white disabled:opacity-30 rounded-none px-6 py-3"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="pr-next-btn"
                type="button"
                onClick={next}
                className="bg-[#29324A] hover:bg-[#1f2638] text-white rounded-none px-6 py-3"
              >
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="pr-submit-btn"
                type="button"
                onClick={submit}
                disabled={submitting || !data.consent}
                className="bg-[#C43A89] hover:bg-[#a22b6e] text-white rounded-none px-6 py-3 disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit My PR Assessment"}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
